import { Inbox } from 'lucide-react';
import { ENUM_LABELS, type PatientDetail, PIN_PREFIX, displayPin } from '#shared/types';
import { Banner } from '#client/components/ui/Banner';
import { Button } from '#client/components/ui/Button';
import { Card } from '#client/components/ui/Card';
import { DataTable } from '#client/components/ui/DataTable';
import { DetailList } from '#client/components/ui/DetailList';
import { EmptyState } from '#client/components/ui/EmptyState';
import { Identifier } from '#client/components/ui/Identifier';
import { PaymentStatusBadge } from '#client/components/ui/StatusBadge';
import { dash, formatAge, formatDateTime, formatMoney } from '#client/lib/format';
import { patientReportColumns } from './patient-columns';
import { listBillingLines } from './patient-record.logic';

/** The strip read out on the phone: identity in one line, not spread across a grid. */
export function PatientIdentityStrip({ patient }: { patient: PatientDetail }) {
  return (
    <span className="c-identity">
      <Identifier value={patient.pin_no} stripPrefix={PIN_PREFIX} copyable />
      <Identifier value={patient.mr_no} copyable />
      <span>{formatAge(patient.age_year, patient.age_month, patient.age_days)}</span>
      <span>{ENUM_LABELS.gender[patient.gender]}</span>
      <span className="tnum">{patient.phone_number}</span>
      <PaymentStatusBadge status={patient.payment_status} />
      {patient.balance > 0 && (
        <span className="c-identity__balance">{formatMoney(patient.balance)} due</span>
      )}
    </span>
  );
}

interface JustRegisteredBannerProps {
  patient: PatientDetail;
  onDismiss: () => void;
  onPrintSlip: () => void;
}

export function JustRegisteredBanner({
  patient,
  onDismiss,
  onPrintSlip,
}: JustRegisteredBannerProps) {
  return (
    <Banner tone="success" title="Registered" onDismiss={onDismiss}>
      {patient.name} is registered as {displayPin(patient.pin_no)}.{' '}
      <Button variant="secondary" size="sm" onClick={onPrintSlip}>
        Print slip
      </Button>
    </Banner>
  );
}

interface PatientReportsCardProps {
  patient: PatientDetail;
  canAddTests: boolean;
  onAddTests: () => void;
  onOpenReport: (reportId: number) => void;
}

export function PatientReportsCard({
  patient,
  canAddTests,
  onAddTests,
  onOpenReport,
}: PatientReportsCardProps) {
  return (
    <Card title={`Reports (${patient.reports.length})`} flush>
      <DataTable
        caption={`Reports for ${patient.name}`}
        columns={patientReportColumns}
        rows={patient.reports}
        rowKey={(row) => row.id}
        stickyHeader={false}
        emptyState={
          <EmptyState
            icon={<Inbox />}
            title="No reports for this patient"
            body="Reports are created from the lab tests chosen during registration."
            action={canAddTests ? <Button onClick={onAddTests}>Add lab tests</Button> : undefined}
          />
        }
        onRowClick={(row) => onOpenReport(row.id)}
      />
    </Card>
  );
}

export function PatientBillingAndDetails({ patient }: { patient: PatientDetail }) {
  return (
    <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_minmax(0,2fr)]">
      <Card title="Billing">
        <DetailList items={listBillingLines(patient)} />
      </Card>

      <Card title="Details">
        <DetailList
          items={[
            { label: 'MR number', value: patient.mr_no, mono: true },
            {
              label: 'Marital status',
              value: ENUM_LABELS.maritalStatus[patient.married_status],
            },
            { label: 'Email', value: patient.email },
            { label: 'CNIC', value: dash(patient.cnic_number), mono: true },
            { label: 'Referred by', value: dash(patient.refer_by) },
            { label: 'Registered', value: formatDateTime(patient.created_at) },
            { label: 'Note', value: dash(patient.note), wide: true },
          ]}
        />
      </Card>
    </div>
  );
}
